import { Link } from 'react-router-dom';
import { t } from 'i18next';
import { LeafBadge, SquareBadge, ThumbnailPhotoCard } from '@peacecover/component';
import { MatchingFill } from '@peacecover/icon';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import { getPhotoCardBadges } from '../../trading/utils/getPhotoCardBadges';
import type { ReviewableTradingPhotoCardType, ReviewableType } from '../types/ReviewableType';

interface MatchingTradingPhotoCardProps {
  photoCard: ReviewableType;
}

const MatchingTradingPhotoCard = ({ photoCard }: MatchingTradingPhotoCardProps) => {
  const { sourceTradingPhotoCard, targetTradingPhotoCard } = photoCard;
  const { userProfile } = targetTradingPhotoCard as ReviewableTradingPhotoCardType;
  const badges = getPhotoCardBadges(targetTradingPhotoCard?.photoCard);

  return (
    <Link
      to={`/reviews/add-review?reviewerId=${userProfile.userProfileId}&reviewableId=${photoCard.reviewableId}&reviewableType=MatchedTradingPhotoCard`}
      className="rounded-[20px] border border-separator2 dark:border-separator1"
    >
      <div className="flex items-center px-[20px] py-[12px] bg-[rgba(0,0,0,0.02)] dark:bg-[rgba(255,255,255,0.02)]">
        <div className="flex items-center gap-[8px]">
          <ProfileAvatar
            thumbnailUrl={userProfile.thumbnailUrl}
            widthAndHeight="28px"
            svgWidthAndHeight="28px"
          />
          <p className="text-[16px] font-semibold text-text-main-lv1 max-w-[138px] truncate mr-[4px] leading-none">
            {userProfile.nickname}
          </p>
        </div>
        <SquareBadge
          styleVariant="solid"
          colorVariant="secondary"
          className="px-[5px] py-[3px] text-[12px]"
        >
          {t('SelectTradingPhotoCardReviewPage.category.exchange')}
        </SquareBadge>
      </div>
      <div className="px-[16px] py-[20px]">
        <div className="flex items-center gap-[8px]">
          <div className="relative">
            <ThumbnailPhotoCard
              imageUrl={
                sourceTradingPhotoCard?.photoCard.mediumThumbnailUrl ||
                sourceTradingPhotoCard?.photoCard.pictureUrl ||
                ''
              }
              className="w-[48px] h-[77px]"
            />
            <LeafBadge
              size="small"
              state="inactive"
              position="left"
              text={t('SelectTradingPhotoCardReviewPage.me') as string}
              className="absolute left-0 bottom-0 bg-pure-black dark:bg-pure-white text-pure-white dark:text-pure-black"
            />
          </div>
          <MatchingFill size={16} className="text-text-sub-lv1" />
          <div className="flex items-center gap-[8px]">
            <div className="relative">
              <ThumbnailPhotoCard
                imageUrl={
                  targetTradingPhotoCard?.photoCard.mediumThumbnailUrl ||
                  targetTradingPhotoCard?.photoCard.pictureUrl ||
                  ''
                }
                className="w-[48px] h-[77px]"
              />
              <LeafBadge
                size="small"
                state="inactive"
                position="left"
                text={t('SelectTradingPhotoCardReviewPage.other') as string}
                className="absolute left-0 bottom-0 bg-pure-black dark:bg-pure-white text-pure-white dark:text-pure-black"
              />
            </div>
            <div className="flex flex-col gap-[8px]">
              <div className="flex flex-col items-start gap-[4px] leading-tight">
                <p className="text-[15px] font-semibold text-text-main-lv2 max-w-[138px] truncate">
                  {t('SelectTradingPhotoCardReviewPage.tradingPhotoCardName', {
                    nickname: targetTradingPhotoCard?.photoCard.memberName,
                    categoryName: t(
                      `PhotoCardCategory.${targetTradingPhotoCard?.photoCard.eventCategory}.${targetTradingPhotoCard?.photoCard.category}.text`
                    ),
                    emoji: t(
                      `PhotoCardCategory.${targetTradingPhotoCard?.photoCard.eventCategory}.${targetTradingPhotoCard?.photoCard.category}.emoji`
                    )
                  })}
                </p>
                <p className="text-[13px] font-medium text-text-main-lv2">
                  {targetTradingPhotoCard?.photoCard.eventName}
                </p>
              </div>
              {badges.length > 0 && (
                <SquareBadge
                  key={badges[0]}
                  styleVariant="line"
                  colorVariant="gray"
                  className="px-[5px] py-[3px] text-text-main-lv4 max-w-[135px] truncate block"
                >
                  {badges[0]}
                </SquareBadge>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default MatchingTradingPhotoCard;
